import type { Auth, User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';

const DEFAULT_DIAGNOSTIC_UNIT_ID = 'D4Av9fGFhTyU26AiQzOY';
const DIAGNOSTIC_FLAG = 'mintleaf.rulesDiagnostics';

type DiagnosticStatus = 'success' | 'not-found' | 'permission-denied' | 'error';

type DiagnosticReadResult = {
  path: string;
  status: DiagnosticStatus;
  code?: string;
  message?: string;
};

type AccessProfileDiagnostic = {
  appUserId: string | null;
  canonicalUserId: string | null;
  unitIds: string[];
  readStatus: DiagnosticStatus;
};

type RulesDiagnosticReport = {
  authUid: string | null;
  currentUserId: string | null;
  accessProfile: AccessProfileDiagnostic;
  reads: DiagnosticReadResult[];
  rootCause:
    | 'accessProfileExists() == false'
    | 'hasAnyUserUnit() == false'
    | 'accessData().unitIds is list == false'
    | 'accessUnits().size() > 0 == false'
    | `accessUnits().hasAny(["${string}"]) == false`
    | `userHasUnit("${string}") == false`
    | 'request.auth missing'
    | 'other';
};

declare global {
  interface Window {
    __mintleafRunRulesDiagnostics?: (unitId?: string) => Promise<RulesDiagnosticReport>;
  }
}

const normalizeUnitIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((unitId): unitId is string => typeof unitId === 'string' && unitId.trim().length > 0);
  }
  return typeof value === 'string' && value.trim().length > 0 ? [value] : [];
};

const classifyFirestoreRead = async (db: Firestore, path: string): Promise<DiagnosticReadResult> => {
  try {
    const snap = await getDoc(doc(db, path));
    return { path, status: snap.exists() ? 'success' : 'not-found' };
  } catch (error: any) {
    const code = typeof error?.code === 'string' ? error.code.replace(/^firestore\//, '') : 'unknown';
    return {
      path,
      status: code === 'permission-denied' ? 'permission-denied' : 'error',
      code,
      message: typeof error?.message === 'string' ? error.message : String(error),
    };
  }
};

const readAccessProfileDiagnostics = async (
  db: Firestore,
  authUid: string | null
): Promise<AccessProfileDiagnostic> => {
  if (!authUid) {
    return {
      appUserId: null,
      canonicalUserId: null,
      unitIds: [],
      readStatus: 'permission-denied',
    };
  }

  try {
    const snap = await getDoc(doc(db, 'access_profiles', authUid));
    if (!snap.exists()) {
      return {
        appUserId: null,
        canonicalUserId: null,
        unitIds: [],
        readStatus: 'not-found',
      };
    }
    const data = snap.data();
    return {
      appUserId: typeof data.appUserId === 'string' ? data.appUserId : null,
      canonicalUserId: typeof data.canonicalUserId === 'string' ? data.canonicalUserId : null,
      unitIds: [
        ...normalizeUnitIds(data.unitIds),
        ...normalizeUnitIds(data.unitIDs),
        ...normalizeUnitIds(data.unitID),
        ...normalizeUnitIds(data.unitId),
      ],
      readStatus: 'success',
    };
  } catch (error: any) {
    const code = typeof error?.code === 'string' ? error.code.replace(/^firestore\//, '') : 'unknown';
    return {
      appUserId: null,
      canonicalUserId: null,
      unitIds: [],
      readStatus: code === 'permission-denied' ? 'permission-denied' : 'error',
    };
  }
};

const inferRootCause = (
  currentUser: FirebaseUser | null,
  _unitId: string,
  _reads: DiagnosticReadResult[]
): RulesDiagnosticReport['rootCause'] => {
  if (!currentUser) return 'request.auth missing';
  return 'other';
};

export const runFirestoreRulesDiagnostics = async (
  auth: Auth,
  db: Firestore,
  unitId = DEFAULT_DIAGNOSTIC_UNIT_ID
): Promise<RulesDiagnosticReport> => {
  const currentUser = auth.currentUser;
  const authUid = currentUser?.uid || null;
  // Firestore Rules diagnostic probe documents were removed to reduce rules complexity.
  // Keep identity/access-profile diagnostics, but do not issue rulesDiagnostics reads.
  const paths: string[] = [];

  const [accessProfile, reads] = await Promise.all([
    readAccessProfileDiagnostics(db, authUid),
    Promise.all(paths.map(path => classifyFirestoreRead(db, path))),
  ]);

  const report: RulesDiagnosticReport = {
    authUid,
    currentUserId: accessProfile.appUserId || accessProfile.canonicalUserId,
    accessProfile,
    reads,
    rootCause: inferRootCause(currentUser, unitId, reads),
  };

  console.group('[rules-diagnostics]');
  console.info('[rules-diagnostics] identity', {
    requestAuthUid: report.authUid,
    currentUserId: report.currentUserId,
    accessProfileAppUserId: report.accessProfile.appUserId,
    accessProfileCanonicalUserId: report.accessProfile.canonicalUserId,
    accessProfileUnitIds: report.accessProfile.unitIds,
    accessProfileReadStatus: report.accessProfile.readStatus,
  });
  report.reads.forEach(read => {
    console.info('[rules-diagnostics] read', read);
  });
  console.info('[rules-diagnostics] rootCause', report.rootCause);
  console.groupEnd();

  return report;
};

export const installFirestoreRulesDiagnostics = (auth: Auth, db: Firestore): void => {
  if (typeof window === 'undefined') return;

  window.__mintleafRunRulesDiagnostics = (unitId = DEFAULT_DIAGNOSTIC_UNIT_ID) =>
    runFirestoreRulesDiagnostics(auth, db, unitId);

  if (window.localStorage.getItem(DIAGNOSTIC_FLAG) === '1') {
    onAuthStateChanged(auth, user => {
      if (user) void runFirestoreRulesDiagnostics(auth, db);
    });
  }
};
